// Founder Commercial Amendment — FORGEDOPS PLANS STARTER (2026-09-XX).
// Company Admin usage-meter card. Reads GET /billing/usage-summary,
// which is a LIVE, archive-aware computation (not a stale cache) --
// this always matches what the server will actually enforce.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Users, FolderKanban, FileText, HardDrive, Cpu, MessageCircleQuestion } from "lucide-react";

const STATUS_META = {
  ok: { bar: "bg-[var(--fop-primary)]", text: "text-[var(--fop-text-3)]" },
  warning: { bar: "bg-[var(--st-warn,#e0a030)]", text: "text-[var(--st-warn,#e0a030)]" },
  critical: { bar: "bg-[var(--st-warn,#e0a030)]", text: "text-[var(--st-warn,#e0a030)]" },
  exceeded: { bar: "bg-[var(--st-escalated)]", text: "text-[var(--st-escalated)]" },
  unlimited: { bar: "bg-[var(--fop-border-2)]", text: "text-[var(--fop-text-3)]" },
  // Founder Post-Deployment Repair Directive (2026-09-29) -- distinct
  // from "unlimited": this account has NO plan/trial at all. Must
  // never read the same as unlimited usage.
  no_plan: { bar: "bg-[var(--st-escalated)]", text: "text-[var(--st-escalated)]" },
  // Founder Sequential Repair Directive item 07 (2026-09-30) -- distinct
  // from "unlimited": this dimension has no cap only because the
  // Founder hasn't approved a number yet (enterprise/custom). Must
  // never read as an intentional unlimited allowance.
  pending_config: { bar: "bg-[var(--fop-border-2)]", text: "text-[var(--fop-text-3)]" },
};

const ROWS = [
  { key: "users", Icon: Users, label: "Users" },
  { key: "active_projects", Icon: FolderKanban, label: "Active Projects" },
  { key: "active_pages", Icon: FileText, label: "Active Plan Pages" },
  { key: "storage_gb", Icon: HardDrive, label: "Storage", unit: "GiB" },
  { key: "ask_queries", Icon: MessageCircleQuestion, label: "Ask Queries (this team, this month)" },
];

function UsageRow({ row, data }) {
  const meta = STATUS_META[data.status] || STATUS_META.ok;
  const pct = data.cap ? Math.min(100, Math.round((data.current / data.cap) * 100)) : 0;
  return (
    <div data-testid={`usage-row-${row.key}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-xs text-[var(--fop-text-2)]">
          <row.Icon size={13} />
          {row.label}
        </div>
        <div className={`text-xs font-mono ${meta.text}`} data-testid={`usage-value-${row.key}`}>
          {data.current}{row.unit ? ` ${row.unit}` : ""}
          {data.status === "no_plan"
            ? " / No plan"
            : data.status === "pending_config"
            ? " / Pending pricing"
            : data.cap
            ? ` / ${data.cap}${row.unit ? ` ${row.unit}` : ""}`
            : " / Unlimited"}
        </div>
      </div>
      {data.cap ? (
        <div className="h-1.5 rounded-full bg-[var(--fop-surface-2)] overflow-hidden">
          <div className={`h-full rounded-full ${meta.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <div className="h-1.5 rounded-full bg-[var(--fop-surface-2)]" />
      )}
      {data.reset_at && (
        <div className="text-[10px] text-[var(--fop-text-3)] mt-0.5" data-testid={`usage-reset-${row.key}`}>
          Resets {data.reset_at}
        </div>
      )}
      {data.status === "no_plan" && (
        <div className="text-[10px] text-[var(--st-escalated)] mt-0.5" data-testid={`usage-warning-${row.key}`}>
          No active plan — choose a plan in Settings → Billing to unlock usage
        </div>
      )}
      {data.status === "warning" && (
        <div className="text-[10px] text-[var(--st-warn,#e0a030)] mt-0.5" data-testid={`usage-warning-${row.key}`}>
          Approaching plan limit
        </div>
      )}
      {data.status === "critical" && (
        <div className="text-[10px] text-[var(--st-warn,#e0a030)] mt-0.5" data-testid={`usage-warning-${row.key}`}>
          Almost at plan limit — consider upgrading soon
        </div>
      )}
      {data.status === "exceeded" && (
        <div className="text-[10px] text-[var(--st-escalated)] mt-0.5" data-testid={`usage-warning-${row.key}`}>
          Limit reached — archive an unused item or upgrade
        </div>
      )}
    </div>
  );
}

export function UsageMeterCard() {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/billing/usage-summary").then((r) => setUsage(r.data)).catch(() => setUsage(null));
  }, []);

  if (!usage) return null;

  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="usage-meter-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">
        Plan Usage
      </div>
      <div className="text-sm text-[var(--fop-text)] mb-3.5" data-testid="usage-plan-label">
        {usage.plan_label || (usage.has_plan === false ? "No active plan" : "—")}
      </div>
      <div className="space-y-3.5">
        {ROWS.map((row) => (
          <UsageRow key={row.key} row={row} data={usage[row.key]} />
        ))}
        {usage.processing?.founder_config_required && (
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--fop-text-3)] pt-1 border-t border-[var(--fop-border-2)]"
               data-testid="usage-processing-pending-config">
            <Cpu size={12} />
            Plan-processing usage isn't limited during your trial
          </div>
        )}
        {usage.capacity_pending_founder_config && (
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--fop-text-3)] pt-1 border-t border-[var(--fop-border-2)]"
               data-testid="usage-capacity-pending-config">
            <Cpu size={12} />
            This plan's capacity limits are still being finalized — usage is tracked, not capped, for now
          </div>
        )}
      </div>
    </div>
  );
}
